import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

// Loads dynamic branding from /api/site/branding and injects:
//   - favicon <link> and apple-touch-icon (via Helmet — works fine)
//   - Google Analytics gtag.js (via native DOM — Helmet does NOT execute
//     inline <script> tags reliably, which was silently dropping every
//     gtag('config', ...) call and starving the GA4 property of events)
//   - SPA route-change tracking: fires a page_view every time the user
//     navigates between pages inside the app.
//
// Both are admin-editable via Admin → Homepage → Branding form.
export function BrandingHead() {
  const [b, setB] = useState(null);
  const location = useLocation();
  // Track the previous pathname+search so we can skip the initial mount
  // (gtag('config', ..., { send_page_view: true }) already emits page_view #1).
  const lastPath = useRef(null);

  useEffect(() => {
    api.get("/site/branding").then((r) => setB(r.data)).catch(() => {});
  }, []);

  // GA4 bootstrap — runs once per mounted app after we know the ID.
  useEffect(() => {
    const ga = (b?.ga_measurement_id || "").trim();
    if (!ga) return;
    if (window.__vvsGaLoaded === ga) return;
    window.__vvsGaLoaded = ga;

    // Standard GA4 bootstrap. dataLayer + gtag stub MUST exist before the
    // async library loads, otherwise the library queues nothing and our
    // 'config' call is dropped.
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag("js", new Date());
    // Disable auto page_view — we send it manually on every route change so
    // both the initial load AND SPA navigations are tracked consistently.
    gtag("config", ga, { send_page_view: false });

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga)}`;
    s.setAttribute("data-testid", "gtag-script");
    document.head.appendChild(s);
  }, [b?.ga_measurement_id]);

  // Fire a page_view whenever the SPA route changes (and on the very first
  // ready state once gtag is defined).
  useEffect(() => {
    const ga = (b?.ga_measurement_id || "").trim();
    if (!ga || typeof window.gtag !== "function") return;
    const path = `${location.pathname}${location.search}`;
    if (lastPath.current === path) return;
    lastPath.current = path;
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: path,
      page_title: document.title,
    });
  }, [location.pathname, location.search, b?.ga_measurement_id]);

  if (!b) return null;
  const fav = (b.favicon_url || "").trim();
  const apple = (b.apple_touch_icon_url || fav).trim();

  return (
    <Helmet>
      {fav && <link rel="icon" href={fav} data-testid="dynamic-favicon" />}
      {fav && <link rel="shortcut icon" href={fav} />}
      {apple && <link rel="apple-touch-icon" href={apple} />}
    </Helmet>
  );
}
